import { useRef, useEffect, useCallback } from 'react';

const DELAY = 500;

export function useThrottle<T extends (...args: any[]) => void>(callback: T, delay: number = DELAY) {
  const lastCallRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedArgsRef = useRef<Parameters<T> | null>(null);

  const throttled = useCallback(
    (...args: Parameters<T>) => {
      const now = Date.now();

      if (now - lastCallRef.current >= delay) {
        lastCallRef.current = now;
        callback(...args);
      } else {
        savedArgsRef.current = args;
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        timeoutRef.current = setTimeout(
          () => {
            lastCallRef.current = Date.now();
            if (savedArgsRef.current) callback(...savedArgsRef.current);
            timeoutRef.current = null;
            savedArgsRef.current = null;
          },
          delay - (now - lastCallRef.current),
        );
      }
    },
    [callback, delay],
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
        savedArgsRef.current = null;
      }
    };
  }, []);

  return throttled;
}
